import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Button,
  Checkbox,
  CheckboxProps,
  Input,
  message,
  Radio,
  Table,
  TableColumnsType,
} from "antd";
import { IconActionKey, IconArrowDown } from "assets/svg";
import {
  PAST_INQUIRY_FILTER_SORT,
  INQUIRY_FILTER_USER_TYPE,
} from "common/constants";
import { Inquiry, InquiryFilter } from "common/interfaces";
import PaginateListMobile from "components/PaginateListMobile";
import { Link } from "react-router-dom";
import { isEmpty } from "lodash";
import { parseErrorMessage } from "common/utils";
import CustomBreadCrumb from "components/CustomBreadCrumb";
import { useGetInquiriesQuery } from "reducers/inquiryApi";
import InquiryItem from "components/InquiryItem";
import { UTCToJapanTime } from "common/helper/time.helper";
import { usePagination } from "common/hooks/usePagination";
import { ConversationInquiryStatusText } from "common/enums/ConversationInquiryStatus.enum";
import { useCommonContext } from "contexts/CommonContext";
import { ConversationInquiryTypeText } from "common/enums/ConversationInquiryType.enum";
import { isNew } from "common/helper/status.helper";
import { PAGINATION } from "common/enums/Pagination.enum";

const columns: TableColumnsType<Inquiry> = [
  {
    title: "お問い合わせID",
    dataIndex: "code",
    align: "center",
    render: (value: number, record: Inquiry) => (
      <>
        {isNew(record.created_at) && (
          <span className="bg-primary text-white px-2 py-1 rounded-[6px] mr-1">
            New
          </span>
        )}
        <Link
          to={`/seller/conversation-inquiry/${record.id}`}
          className="text-primary"
        >
          #{value}
        </Link>
      </>
    ),
  },
  {
    title: "件名",
    dataIndex: "subject",
    align: "center",
  },
  {
    title: "種別",
    dataIndex: "type",
    align: "center",
    render: (value) => ConversationInquiryTypeText(value),
  },
  {
    title: "ステータス",
    dataIndex: "status",
    align: "center",
    render: (value) => ConversationInquiryStatusText(value),
  },
  {
    title: "最終更新",
    dataIndex: "created_at",
    align: "center",
    render: (value) => {
      const timeJapan = UTCToJapanTime(value);
      return `${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`;
    },
  },
  {
    title: "操作",
    dataIndex: "id",
    align: "center",
    render: (value: number, record: Inquiry) => (
      <>
        <Link
          to={`/seller/conversation-inquiry/${record.id}`}
          className="text-primary"
        >
          詳細画面へ
        </Link>
      </>
    ),
  },
];

const InquiryList = () => {
  const { setHeaderTitle } = useCommonContext();
  const [showFilter, setShowFilter] = useState(false);
  const [searchValueDebounced, setSearchValueDebounced] = useState("");
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const {
    pagination,
    paginationMobile,
    onChangePage,
    reset: resetPagination,
    resetMobile: resetPaginationMobile,
    updateTotal,
    updateTotalMobile,
  } = usePagination();

  const defaultValues: InquiryFilter = {
    search: "",
    type: [
      INQUIRY_FILTER_USER_TYPE.ABOUT_LIST.value,
      INQUIRY_FILTER_USER_TYPE.ABOUT_TRANSACTION.value,
      INQUIRY_FILTER_USER_TYPE.ABOUT_ISSUE.value,
      INQUIRY_FILTER_USER_TYPE.OTHER.value,
    ],
    sort: PAST_INQUIRY_FILTER_SORT.NEWEST.value,
  };

  const { handleSubmit, control, watch, reset } = useForm<InquiryFilter>({
    defaultValues,
  });

  const inquiryList: InquiryFilter = watch();
  const prevInquiryList = useRef<InquiryFilter>(defaultValues);

  const {
    data: inquiryData,
    error: inquiryErr,
    refetch: refetchInquiry,
  } = useGetInquiriesQuery({
    filter: {
      ...inquiryList,
      search: searchValueDebounced,
    },
    page: pagination.current,
  });

  const {
    data: inquiryMobileData,
    error: inquiryMobileErr,
    refetch: refetchInquiryMobile,
  } = useGetInquiriesQuery({
    filter: {
      ...inquiryList,
      search: searchValueDebounced,
    },
    page: paginationMobile.current,
  });

  const plainTypeOptions = Object.values(INQUIRY_FILTER_USER_TYPE).map(
    (o) => o.value,
  );

  const [checkedTypeList, setCheckedTypeList] =
    useState<string[]>(plainTypeOptions);

  const checkAllType = plainTypeOptions.length === checkedTypeList.length;
  const indeterminateType =
    checkedTypeList.length > 0 &&
    checkedTypeList.length < plainTypeOptions.length;

  const toggleFilter = () => setShowFilter((prev) => !prev);

  const onReset = async () => {
    resetPagination();
    resetPaginationMobile();
    reset();
    setSearchValueDebounced("");
    setCheckedTypeList(plainTypeOptions);
  };

  const onSubmit = async () => {
    resetPagination();
    resetPaginationMobile();
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    setSearchValueDebounced(inquiryList.search);
    setTimeout(() => {
      refetchInquiry();
      refetchInquiryMobile();
    }, 0);
  };

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearchValueDebounced(inquiryList.search);
    }, 2000);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [inquiryList.search]);

  useEffect(() => {
    if (
      JSON.stringify(prevInquiryList.current) !== JSON.stringify(inquiryList)
    ) {
      resetPagination();
      resetPaginationMobile();
      prevInquiryList.current = inquiryList;
    }
  }, [inquiryList]);

  useEffect(() => {
    if (pagination.current === PAGINATION.PAGE) {
      refetchInquiry();
    }
  }, [pagination.current]);

  useEffect(() => {
    if (paginationMobile.current === PAGINATION.PAGE) {
      refetchInquiryMobile();
    }
  }, [paginationMobile.current]);

  useEffect(() => {
    if (inquiryData?.data?.meta?.total_items) {
      updateTotal(inquiryData.data.meta.total_items);
    }
  }, [inquiryData]);

  useEffect(() => {
    if (inquiryMobileData?.data?.meta?.total_items) {
      updateTotalMobile(inquiryMobileData.data.meta.total_items);
    }
  }, [inquiryMobileData]);

  useEffect(() => {
    if (inquiryErr) void message.error(parseErrorMessage(inquiryErr));
  }, [inquiryErr]);

  useEffect(() => {
    if (inquiryMobileErr)
      void message.error(parseErrorMessage(inquiryMobileErr));
  }, [inquiryMobileErr]);

  useEffect(() => {
    setHeaderTitle("過去のお問い合わせ一覧");
  }, []);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          handleSubmit(onSubmit)();
        }
      }}
      className="w-full product-list"
    >
      <div className="flex min-h-[752px] px-5 pt-5 md:p-[40px] pb-[100px] flex-col items-center gap-[30px] self-stretch">
        <div className="flex flex-col items-start gap-[10px] md:gap-[20px] self-stretch">
          <CustomBreadCrumb
            items={[
              {
                label: "お問い合わせ",
                link: "/seller/conversation-inquiry",
              },
              {
                label: `お問い合わせ詳細`,
                isCurrent: true,
              },
            ]}
          />
          <Controller
            name="search"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                prefix={<IconActionKey />}
                placeholder="件名などで検索"
                className="w-full h-[60px] outline outline-[3px] outline-offset-[-3px] outline-primary"
              />
            )}
          />
          {!showFilter && (
            <div
              data-プロパティ1="base"
              className="inline-flex justify-start items-center cursor-pointer"
              onClick={toggleFilter}
            >
              <div className="justify-start text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
                検索条件を追加
              </div>
              <IconArrowDown className="w-[20px] h-[20px] aspect-[1/1]" />
            </div>
          )}
          {showFilter && (
            <div className="self-stretch p-[20px] md:p-10 bg-primary-light rounded-xl inline-flex flex-col justify-center items-center md:gap-7">
              <div className="self-stretch justify-start text-primary text-[18px] md:text-xl font-bold font-['Noto_Sans_JP'] leading-loose">
                検索条件（フィルター）
              </div>
              <div className="self-stretch flex flex-col justify-start items-start">
                <div className="self-stretch py-5 border-b border-white inline-flex flex-col md:flex-row justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    お問い合わせ種別
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start product-category">
                    <Controller
                      control={control}
                      name="type"
                      defaultValue={plainTypeOptions}
                      render={({ field }) => {
                        const onChange = (list: string[]) => {
                          setCheckedTypeList(list);
                          field.onChange(list);
                        };
                        const onCheckAllChange: CheckboxProps["onChange"] = (
                          e,
                        ) => {
                          const newList = e.target.checked
                            ? plainTypeOptions
                            : [];
                          setCheckedTypeList(newList);
                          field.onChange(newList);
                        };
                        return (
                          <>
                            <Checkbox
                              indeterminate={indeterminateType}
                              onChange={onCheckAllChange}
                              checked={checkAllType}
                            >
                              全て
                            </Checkbox>
                            <Checkbox.Group
                              {...field}
                              options={Object.values(INQUIRY_FILTER_USER_TYPE)}
                              value={checkedTypeList}
                              onChange={onChange}
                            />
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="self-stretch py-5 flex-col md:flex-row inline-flex justify-start items-start md:items-center gap-5">
                  <div className="w-48 justify-start text-black text-base font-medium font-['Noto_Sans_JP'] leading-relaxed">
                    並び替え
                  </div>
                  <div className="flex-1 flex flex-col md:flex-row justify-start items-start gap-x-[15px] gap-y-[10px] md:gap-7 flex-wrap content-start">
                    <Controller
                      control={control}
                      name="sort"
                      render={({ field }) => (
                        <Radio.Group
                          {...field}
                          options={Object.values(PAST_INQUIRY_FILTER_SORT)}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="w-full md:w-auto flex-col-reverse md:flex-row inline-flex justify-start items-start gap-[10px] md:gap-5">
                <Button
                  block
                  type="primary"
                  htmlType="reset"
                  onClick={onReset}
                  className="min-w-full md:min-w-[340px] min-h-14 bg-white outline outline-2 outline-offset-[-2px] outline-primary text-primary text-lg font-bold"
                >
                  クリア
                </Button>
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  className="min-w-full md:min-w-[340px] min-h-14 bg-primary text-lg font-bold"
                >
                  検索する
                </Button>
              </div>
            </div>
          )}
          <Table<Inquiry>
            className="w-full hidden md:block"
            rowKey="code"
            onChange={onChangePage}
            pagination={!isEmpty(inquiryData?.data?.items) ? pagination : false}
            columns={columns}
            dataSource={inquiryData?.data?.items || []}
          />
          <PaginateListMobile<Inquiry>
            pagination={
              !isEmpty(inquiryData?.data?.items) ? paginationMobile : false
            }
            dataSource={inquiryMobileData?.data?.items || []}
            renderItem={(item, checked, onSelect) => (
              <InquiryItem<Inquiry>
                inquiry={item}
                checked={checked}
                onSelect={onSelect}
                hideSelect={true}
              />
            )}
          />
        </div>
      </div>
    </form>
  );
};

export default InquiryList;
